import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSetSelectTheme } from "@/contexts/SelectThemeContext";
import { toast } from "sonner";
import { ArrowLeft, Check, Plus, Bell, BellOff, FileCheck2, AlertTriangle, Sparkles } from "lucide-react";
import { isProductOutOfStock } from "@/lib/stock";
import { selectStockBadge } from "@/lib/stockBadge";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDose } from "@/lib/formatDose";
import { ladderPreview } from "@/lib/selectDecompose";

export default function ProductDetail({ slug: propSlug, tier: propTier, embedded = false, featured = false } = {}) {
  const routerParams = useParams();
  const slug = propSlug || routerParams?.slug;
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [supplyKit, setSupplyKit] = useState(3);
  const [variant, setVariant] = useState(null); // "1vial" | "3vial" | "5vial" | null (Supply)
  const [variantMg, setVariantMg] = useState(null); // per-mg vial-strength option
  const [buying, setBuying] = useState(false);
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [waitlistBusy, setWaitlistBusy] = useState(false);
  // iter185.2 — Supply fixed-tier price resolved from `/supply/tiers` when
  // the product carries a `supply_tier_id`. Falls back to product.price if
  // the tier row can't be resolved (legacy Supply products still work).
  const [supplyTierPrice, setSupplyTierPrice] = useState(null);
  // Select-tier vial-quantity discount ladder (fractions, e.g. {3:0.05, 5:0.10}).
  // Populated on mount when the product is Select-tier; used to price the
  // 1/3/5 Vial tiles and render "Save X%" badges.
  const [selectLadder, setSelectLadder] = useState({ 3: 0.05, 5: 0.10 });
  // Select-tier inventory countdown thresholds — fetched from public
  // /landing/settings. Same defaults as the catalog page.
  const [stockThresholds, setStockThresholds] = useState({
    lowStockThreshold: 20,
    limitedStockThreshold: 30,
  });
  const { add } = useCart();
  const { user } = useAuth();
  const isTempViewer = user?.role === "temp_viewer";
  const nav = useNavigate();
  // Tier hint from the URL (?tier=supply|select). Used to keep tier continuity
  // when linking out to the branded /coa/:slug viewer.
  const tier = propTier !== undefined && propTier !== null
    ? propTier
    : (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("tier") || ""
      : "");
  // iter_142 — eliminate the white "bone" flash on Select PDP navigation.
  // Previously `select` was derived only from the (async-loaded) product, so
  // the first paint was in the light theme until the fetch resolved. Now we
  // seed it from the `?tier=select` URL hint (Catalog always links with it)
  // so the dark theme is applied on the very first paint. Once the product
  // actually loads, product.tier wins.
  const select = product ? product.tier === "select" : tier === "select";
  // Activate the R-1617 sub-brand skin when viewing a Select-tier product.
  useSetSelectTheme(select);

  useEffect(() => {
    (async () => {
      try {
        // Read `?tier=` off the URL so the backend can resolve the correct
        // per-tier product when Supply + Select share a slug.
        const tierParam = typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("tier")
          : null;
        const qs = tierParam ? `?tier=${encodeURIComponent(tierParam)}` : "";
        const { data } = await api.get(`/products/${slug}${qs}`);
        // Legacy slug 301: backend returns the canonical product with a
        // `redirected_from` field when the requested slug was retired. Push
        // the browser URL to the canonical /shop/{new-slug} so bookmarks +
        // search results settle on the live URL.
        if (data.redirected_from && data.slug && data.slug !== slug) {
          const preservedTier = tierParam ? `?tier=${encodeURIComponent(tierParam)}` : "";
          nav(`/shop/${data.slug}${preservedTier}`, { replace: true });
          return;
        }
        setProduct(data);
        // Select tier: default vial-quantity tile to "1vial". Legacy per-mg
        // and product-level price_3vial/price_5vial fields are ignored under
        // the new ladder pricing model.
        if (data.tier === "select") {
          setVariant("1vial");
          // Pull the current admin-configured ladder for the "Save X%" badges
          // and tile prices. Silently fall back to the useState default if
          // the endpoint is unreachable. Per-product override wins per tile.
          try {
            const { data: L } = await api.get("/select-ladder");
            const g3 = Number(L.discount_3vial || 0);
            const g5 = Number(L.discount_5vial || 0);
            const ov3 = data.select_ladder_override_3vial;
            const ov5 = data.select_ladder_override_5vial;
            setSelectLadder({
              3: ov3 != null ? Number(ov3) : g3,
              5: ov5 != null ? Number(ov5) : g5,
              10: Number(L.discount_10vial || 0),
            });
          } catch { /* fall back to defaults */ }
          // Pull the countdown thresholds so the PDP badge matches the catalog
          // exactly. Non-fatal — falls back to defaults on error.
          try {
            const { data: S } = await api.get("/landing/settings");
            setStockThresholds({
              lowStockThreshold: Number(S?.low_stock_threshold ?? 20),
              limitedStockThreshold: Number(S?.limited_stock_threshold ?? 30),
            });
          } catch { /* keep defaults */ }
        } else {
          setVariant(null);
        }
        // Default mg-variant: pick the lowest-priced in-stock option, or the
        // lowest-priced option overall if all are OOS.
        const vs = Array.isArray(data.variants) ? data.variants.filter((v) => v && v.price != null) : [];
        if (vs.length > 0) {
          const inStock = vs.filter((v) => v.in_stock !== false);
          const pool = inStock.length > 0 ? inStock : vs;
          const cheapest = pool.reduce((a, b) => (Number(a.price) <= Number(b.price) ? a : b));
          setVariantMg(String(cheapest.mg));
        } else {
          setVariantMg(null);
        }
        if ((data.in_stock === false) || (Array.isArray(data.variants) && data.variants.length > 0) || (data.vials_in_stock != null && Number(data.vials_in_stock) >= 0)) {
          try {
            const { data: w } = await api.get(`/products/${data.id}/notify-me`);
            setOnWaitlist(!!w.on_list);
          } catch {/* fine */}
        }
      } catch {
        nav("/shop", { replace: true });
      }
    })();
  }, [slug, nav]);

  if (!product) return <div className="p-12 overline">Loading…</div>;

  // mg-variants take priority when configured (both tiers).
  const mgVariants = Array.isArray(product.variants) ? product.variants.filter((v) => v && v.price != null) : [];
  const hasMgVariants = mgVariants.length > 0;
  const selectedMgVariant = hasMgVariants
    ? mgVariants.find((v) => String(v.mg) === String(variantMg)) || null
    : null;
  const mgOutOfStock = hasMgVariants && (!selectedMgVariant || selectedMgVariant.in_stock === false);
  // Product-level "out of stock" for buyer-facing UI. Delegates to the shared
  // helper so Catalog cards and this PDP stay in lock-step. Multi-variant
  // products IGNORE product-level `in_stock` — variant-level is the truth.
  const outOfStock = isProductOutOfStock(product);
  const isSupplyKit = product.tier === "supply";
  const supplyKitPrice = (n) => {
    const row = (product.variants || []).length >= 2 ? selectedMgVariant : null;
    const src = row || product;
    return Number((n === 10 ? src.price_10vial : src.price) || 0);
  };

  // ─── Select-tier vial-quantity ladder pricing (2026-02) ──────────────────
  // Single-vial "unit" price: variant.price if mg-variants configured, else
  // product.price. Ladder discount applies per selected tile (1/3/5 vials).
  const selectVialCount = variant === "1vial" ? 1 : variant === "3vial" ? 3 : variant === "5vial" ? 5 : variant === "10vial" ? 10 : 1;
  const selectUnitPrice = select
    ? (hasMgVariants ? (selectedMgVariant ? Number(selectedMgVariant.price) : null) : Number(product.price || 0))
    : null;
  const selectTilePrice = (n) => {
    if (!select || selectUnitPrice == null) return null;
    const disc = Number(selectLadder[n] || 0);
    return +(selectUnitPrice * n * (1 - disc)).toFixed(2);
  };

  // Supply-tier: prefer the resolved fixed-tier price (from /supply/tiers)
  // when the product is bound to a supply_tier_id; otherwise fall back to
  // the flat product.price (legacy Supply products).
  const displayPrice = select
    ? (selectTilePrice(selectVialCount) ?? 0)
    : isSupplyKit
      ? supplyKitPrice(supplyKit)
      : (hasMgVariants && selectedMgVariant
        ? Number(selectedMgVariant.price)
        : (supplyTierPrice != null ? supplyTierPrice : Number(product.price || 0)));

  const cannotAdd = outOfStock
    || (isSupplyKit && !(displayPrice > 0))
    || (hasMgVariants && mgOutOfStock)
    || (select && selectUnitPrice == null);

  // iter207 · Granny-proof pricing preview. Total vials the buyer will
  // actually receive = pdpTile.n × qty. Decomposition + total price come
  // from the authoritative ladder + per-product overrides already fetched
  // above — same values feeding the tile badges and backend
  // `_resolve_select_line_price`. `null` for non-Select or when the
  // decomposition adds no ladder discount (e.g. qty=1 on 1-vial).
  const totalVials = select ? selectVialCount * qty : 0;
  const preview = select && selectUnitPrice != null && totalVials > 0 && variant !== "10vial"
    ? ladderPreview(totalVials, selectUnitPrice, selectLadder)
    : null;
  const decomposedTotal = select && preview ? preview.totalPrice : (displayPrice * qty);
  const canDecompose = select && !product.single_buy && !product.max_qty_per_order;

  const toggleWaitlist = async () => {
    setWaitlistBusy(true);
    try {
      if (onWaitlist) {
        await api.delete(`/products/${product.id}/notify-me`);
        setOnWaitlist(false);
        toast.success("Removed from waitlist");
      } else {
        await api.post(`/products/${product.id}/notify-me`);
        setOnWaitlist(true);
        toast.success("We'll email you the moment it's back.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setWaitlistBusy(false);
    }
  };

  return (
    <div className={embedded ? "max-w-[1100px] mx-auto" : "max-w-[1100px] mx-auto px-6 lg:px-12 py-10 lg:py-16"} data-testid={`product-page-${product.slug}`}>
      {!embedded && (
      <Link to="/shop" className={`text-[13px] uppercase tracking-[0.22em] inline-flex items-center gap-2 mb-10 transition-colors ${select ? "text-[#6E521A] hover:text-[#6E521A]" : "text-stone hover:text-charcoal"}`} data-testid="back-to-catalog">
        <ArrowLeft size={12} strokeWidth={1.5}/> Back to catalog
      </Link>
      )}

      <div className="grid lg:grid-cols-[1fr_1fr] gap-8 lg:gap-16 items-start">
        {/* Left: image panel. Select-tier products with a `hero_image_url` get
            a portrait treatment (4:5, warm bone gradient background, no dark
            charcoal card) for a cleaner presentation. Every other case keeps
            the original charcoal-carded product image. */}
        {select && product.hero_image_url && !featured ? (
          <div
            className={`relative rounded-2xl aspect-[4/5] overflow-hidden bg-gradient-to-b from-[#F5EAD4] via-[#EBD9B1] to-[#D9C08A] shadow-[0_30px_60px_-30px_rgba(74,14,23,0.35)] ring-1 ring-[#B08A45]/20 ${outOfStock ? "opacity-80" : ""}`}
            data-testid="product-hero-editorial"
          >
            <img
              src={product.hero_image_url}
              alt={product.name}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="product-hero-image"
            />
            {/* Soft-lit vignette so the vial feels lit from above rather than
                floating on flat cream. Kept subtle — 12% top/22% bottom. */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/12 via-transparent to-black/22" />
            <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap pointer-events-none z-10">
              {featured && (
                <span className={`inline-flex items-center gap-1.5 text-[12px] font-mono font-bold uppercase tracking-[0.24em] px-3.5 py-1.5 rounded-full shadow-lg ring-1 ring-black/10 ${select ? "bg-[#DDA94E] text-black" : "bg-oxblood text-bone"}`} data-testid="pd-featured-badge">
                  ★ Featured
                </span>
              )}
            </div>
          </div>
        ) : product.image_url ? (
          // Select-tier: NON-INTERACTIVE image tile (no lightbox), photo fills
          // the box edge-to-edge (no inner padding) so black-bg product photos
          // sit flush with the black card and don't reveal a padding seam. A
          // subtle gold hairline frames the tile. Supply keeps the zoom flow.
          select ? (
            <div className={`relative rounded-2xl aspect-square overflow-hidden select-photo-well ${outOfStock ? "opacity-80" : ""}`}>
              <img
                src={product.image_url}
                alt={product.name}
                className="absolute inset-0 w-full h-full object-contain p-10 md:p-12"
                data-testid="product-hero-image"
              />
              {/* Informational chips at top-left */}
              <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap pointer-events-none z-10">
                <span className="hidden bg-charcoal text-bone text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full">
                  {product.sku}
                </span>
                {featured && (
                  <span className={`inline-flex items-center gap-1.5 text-[12px] font-mono font-bold uppercase tracking-[0.24em] px-3.5 py-1.5 rounded-full shadow-lg ring-1 ring-black/10 ${select ? "bg-[#DDA94E] text-black" : "bg-oxblood text-bone"}`} data-testid="pd-featured-badge">
                    ★ Featured
                  </span>
                )}
              </div>
            </div>
          ) : (
          <div className={`relative rounded-2xl aspect-square overflow-hidden bg-charcoal ${outOfStock ? "opacity-80" : ""}`}>
            <img
              src={product.image_url}
              alt={product.name}
              className="absolute inset-0 w-full h-full object-contain p-10 md:p-12"
              data-testid="product-hero-image"
            />
            {/* Informational chips at top-left — kept because they don't collide with the bottle center */}
            <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap pointer-events-none z-10">
              {/* SKU pill hidden on public. Kept in DOM for testid regressions. */}
              <span className="hidden bg-charcoal text-bone text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full">
                {product.sku}
              </span>
              {featured && (
                <span className={`inline-flex items-center gap-1.5 text-[12px] font-mono font-bold uppercase tracking-[0.24em] px-3.5 py-1.5 rounded-full shadow-lg ring-1 ring-black/10 ${select ? "bg-[#DDA94E] text-black" : "bg-oxblood text-bone"}`} data-testid="pd-featured-badge">
                  ★ Featured
                </span>
              )}
            </div>
          </div>
          )
        ) : (
          // No image: text-only "hero card". Reduced weight — smaller type,
          // less padding, capped height so it doesn't overpower the details
          // column. Theme-aware so Select renders in dark/gold, not cream.
          <div className={`relative rounded-2xl flex flex-col justify-between overflow-hidden ${outOfStock ? "opacity-80" : ""} ${select ? "bg-[#EBE0BE] border border-[#DDA94E] p-5 md:p-6 aspect-[5/4] md:aspect-[4/3] min-h-[260px]" : "bg-[#EFEAE0] p-6 md:p-8 aspect-[5/4] md:aspect-[4/3] min-h-[380px]"}`}>
            <div className="flex items-center gap-2 flex-wrap">
              {/* SKU pill hidden on public. Kept in DOM for testid regressions. */}
              <span className={`hidden text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full ${select ? "bg-[#EBE0BE] border border-[#DDA94E]/40 text-[#1B1614]" : "bg-charcoal text-bone"}`}>
                {product.sku}
              </span>
              {featured && (
                <span className={`inline-flex items-center gap-1.5 text-[12px] font-mono font-bold uppercase tracking-[0.24em] px-3.5 py-1.5 rounded-full shadow-lg ring-1 ring-black/10 ${select ? "bg-[#DDA94E] text-black" : "bg-oxblood text-bone"}`} data-testid="pd-featured-badge">
                  ★ Featured
                </span>
              )}
            </div>
            <div>
              <h1 className={`font-serif tracking-tight leading-tight ${select ? "text-[#1B1614] text-2xl md:text-3xl" : "text-3xl md:text-4xl"}`}>{product.name}</h1>
              <p className={`text-xs mt-2 ${select ? "text-[#6E521A]" : "text-stone"}`}>{product.tier === "supply" ? `${product.kit_vial_count || 3} vials/kit` : product.unit_label}</p>
            </div>
            <div className="flex items-center justify-end">
              {/* Price removed from the hero card — it lives on the right column where the buyer decides.
                  Only the category chip remains here as a subtle anchor. */}
              <span className={`text-[12px] font-mono uppercase tracking-[0.2em] ${select ? "text-[#6E521A]" : "text-charcoal/85"}`}>{product.category}</span>
            </div>
          </div>
        )}

        {/* Right: details */}
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {!featured && (
              <span className={`text-[13px] font-mono uppercase tracking-[0.22em] font-medium ${select ? "text-[#6E521A]" : "text-oxblood"}`}>
                {product.category}
              </span>
            )}
            {/* Category · SKU separator + SKU chip hidden on public (admin-only field). */}
            <span className={`hidden ${select ? "text-[#6E521A]" : "text-stone"}`}>·</span>
            <span className={`hidden text-[13px] font-mono uppercase tracking-[0.22em] ${select ? "text-[#6E521A]" : "text-stone"}`}>
              {product.sku}
            </span>
            {(product.is_hot_seller || featured) && (
              <span
                className={`inline-flex items-center gap-1 text-[13px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full ${featured ? "order-1" : ""} ${select ? "bg-[#DDA94E] text-black" : "bg-oxblood text-bone"}`}
                data-testid="pd-hot-seller-badge"
                title="Hot seller"
              >
                🔥 Hot Seller!
              </span>
            )}
            {/* iter156.10 — Source pill (In-House vs Supplier) removed at
                the site owner's direction. All BBK products are supplier-
                sourced; the In-House label was factually incorrect and the
                distinction is no longer surfaced to buyers. */}
            {/* iter156.9 → iter205 — Testing status pill only renders for TESTED
                products on SUPPLY. On SELECT the badge is redundant (testing is
                inherent to the SELECT tier and the COA badge already carries
                the documentation signal), so it's hidden there. All other
                badges in the row (HOT SELLER, COA · N, LYO USA) unchanged. */}
            {product.testing_status === "tested" && !select && (
              <span
                className={`inline-flex items-center text-[12px] font-mono uppercase tracking-[0.22em] px-2.5 py-0.5 rounded-full border ${
                  select
                    ? "bg-emerald-950 border-emerald-500/50 text-emerald-300"
                    : "bg-emerald-700 border-emerald-700 text-bone"
                }`}
                data-testid="pd-testing-pill"
                title="Purity and potency verified"
              >
                Tested
              </span>
            )}
            {/* PDP top-of-page COA badge — matches the green Catalog card
                treatment. Single-COA opens the PDF directly; multi-COA
                exposes the same hover chooser used on Catalog cards. */}
            {product.tier !== "supply" && ((product.coas || []).length > 0 || featured) && (() => {
              const API = process.env.REACT_APP_BACKEND_URL || "";
              const coas = product.coas || [];
              const direct = (coa) => `${API}/api/products/${product.id}/coa/${coa.id}`;
              const cls = `inline-flex items-center gap-1.5 text-[12px] font-mono font-medium uppercase tracking-[0.14em] px-3 py-1 rounded-full transition-colors shadow-sm ${featured ? "order-3" : ""} ${
                select
                  ? "bg-[#0F5F3B] hover:bg-[#0A4A2C] text-bone border border-[#0F5F3B]"
                  : "bg-emerald-700 hover:bg-emerald-800 text-bone border border-emerald-800"
              }`;
              if (featured && coas.length === 0) {
                return (
                  <span
                    className={cls}
                    data-testid="pd-coa-badge"
                    aria-disabled="true"
                    title="COA"
                  >
                    <Check size={12} strokeWidth={2.5} />
                    COA
                  </span>
                );
              }
              if (coas.length === 1) {
                return (
                  <a
                    href={direct(coas[0])}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cls}
                    data-testid="pd-coa-badge"
                    title="View Certificate of Analysis (opens in new tab)"
                  >
                    <Check size={12} strokeWidth={2.5} />
                    COA
                  </a>
                );
              }
              return (
                <HoverCard openDelay={80} closeDelay={120}>
                  <HoverCardTrigger asChild>
                    <button
                      type="button"
                      className={cls}
                      data-testid="pd-coa-badge"
                      title="Choose a Certificate of Analysis"
                    >
                      <Check size={12} strokeWidth={2.5} />
                      COA
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent align="start" className="p-2 w-[260px] bg-white border-borderSubtle">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-charcoal/60 px-2 pt-1 pb-2">
                      Choose certificate
                    </div>
                    <div className="flex flex-col gap-1">
                      {coas.map((c) => (
                        <a
                          key={c.id}
                          href={direct(c)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-1.5 text-[12px] text-charcoal hover:bg-bone rounded-sm truncate"
                          data-testid={`pd-coa-choice-${c.id}`}
                          title={c.filename || `COA ${c.id}`}
                        >
                          {c.filename || `Certificate ${c.id}`}
                        </a>
                      ))}
                    </div>
                  </HoverCardContent>
                </HoverCard>
              );
            })()}
            {(select || featured) && product.is_lyo_usa && (
              <span
                className={`inline-flex items-center gap-1 text-[12px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full ${featured ? "order-2" : ""} ${select ? "bg-[#EBE0BE] border border-[#DDA94E]/50 text-[#1B1614]" : "bg-white border border-borderSubtle text-charcoal"}`}
                data-testid="pd-lyo-usa-badge"
                title="US Lyophilization"
                aria-label="Lyophilized in the USA"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden style={{ display: "inline-block" }}>
                  <defs>
                    <clipPath id="pd-flag-clip"><circle cx="12" cy="12" r="11" /></clipPath>
                  </defs>
                  <g clipPath="url(#pd-flag-clip)">
                    <rect x="0" y="0" width="24" height="24" fill="#F5EFE5" />
                    {[1,3,5,7,9,11].map((y) => <rect key={y} x="0" y={y*2} width="24" height="2" fill="#B22234" />)}
                    <rect x="0" y="0" width="11" height="12" fill="#3C3B6E" />
                  </g>
                </svg>
                Lyo USA
              </span>
            )}
          </div>
          <h1 className={`font-serif tracking-tight leading-none ${select ? "text-[#1B1614] text-3xl md:text-4xl mb-4" : "text-5xl md:text-6xl mb-4"}`} data-testid="product-name">{select ? String(product.name || "").replace(/\s*\d+\s*mg\s*$/i, "").trim() : product.name}</h1>
          {select ? (
            <p className={`text-[14px] leading-relaxed text-[#1B1614]/85 mb-2`} data-testid="select-research-disclaimer">
              These products are sold exclusively for laboratory research use only. They are not intended for human or animal consumption, clinical use, or veterinary applications, and are not intended to diagnose, treat, cure, or prevent any disease.
            </p>
          ) : (
            <>
              <p className={`text-[13px] uppercase tracking-[0.22em] text-charcoal mb-6`}>Research compound · {product.dose_mg ? `${product.dose_mg} · ` : ""}{product.tier === "supply" ? `${product.kit_vial_count || 3} vials/kit` : product.unit_label}</p>
              <p className={`text-[14px] leading-relaxed text-charcoal/85 mb-6`} data-testid="supply-research-disclaimer">
                These products are sold exclusively for laboratory research use only. They are not intended for human or animal consumption, clinical use, or veterinary applications, and are not intended to diagnose, treat, cure, or prevent any disease.
              </p>
              {product.tier !== "supply" && (
                <p className={`font-mono mb-10 text-oxblood text-4xl`} data-testid="product-price">${displayPrice.toFixed(2)}<span className={`text-[15px] ml-2 font-sans text-charcoal`}>/ {hasMgVariants ? "vial" : "kit"}</span></p>
              )}
              {product.tier === "supply" && (product.coas || []).length > 0 && (() => {
                const API = process.env.REACT_APP_BACKEND_URL || "";
                const coas = product.coas || [];
                const direct = (c) => `${API}/api/products/${product.id}/coa/${c.id}`;
                const linkCls = "inline-flex items-center text-[12px] font-mono uppercase tracking-[0.2em] font-semibold text-oxblood bg-transparent border border-oxblood/70 hover:bg-oxblood hover:text-bone rounded-sm px-3 py-1.5 mb-10 transition-colors";
                if (coas.length === 1) {
                  return (
                    <a
                      href={direct(coas[0])}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkCls}
                      data-testid="pdp-view-coa"
                      title="View Certificate of Analysis (opens in new tab)"
                    >
                      View COA
                    </a>
                  );
                }
                return (
                  <HoverCard openDelay={80} closeDelay={120}>
                    <HoverCardTrigger asChild>
                      <button type="button" className={linkCls} data-testid="pdp-view-coa" title="Choose a Certificate of Analysis">View COA</button>
                    </HoverCardTrigger>
                    <HoverCardContent align="start" className="p-2 w-[260px] bg-white border-borderSubtle">
                      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-charcoal/60 px-2 pt-1 pb-2">Choose certificate</div>
                      <div className="flex flex-col gap-1">
                        {coas.map((c) => (
                          <a
                            key={c.id}
                            href={direct(c)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-1.5 text-[12px] text-charcoal hover:bg-bone rounded-sm truncate"
                            data-testid={`pdp-view-coa-choice-${c.id}`}
                            title={c.filename || `COA ${c.id}`}
                          >
                            {c.filename || `Certificate ${c.id}`}
                          </a>
                        ))}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                );
              })()}
              {product.single_buy && (
                <div
                  className="mb-10 -mt-6 inline-flex items-start gap-2 border border-charcoal/85 bg-white px-3 py-2 rounded-md"
                  data-testid="single-buy-callout"
                >
                  <span className="text-[12px] uppercase tracking-[0.22em] font-mono text-charcoal font-bold">No minimum</span>
                  <span className="text-[14px] text-charcoal leading-snug">
                    Order a single unit — this product is exempt from the tier's minimum-kit rule.
                  </span>
                </div>
              )}
            </>
          )}

          {!select && product.tier !== "supply" && Array.isArray(product.spec) && product.spec.length > 0 && (
            <>
              <div className="hairline my-8" />
              <p className="text-[13px] uppercase tracking-[0.22em] mb-4 text-charcoal font-medium" data-testid="spec-sheet-heading">Spec sheet</p>
              <dl className="mb-8 grid grid-cols-[max-content,1fr] gap-x-6 gap-y-2.5" data-testid="spec-sheet-table">
                {product.spec.map((row, idx) => (
                  <React.Fragment key={idx}>
                    <dt className="text-[13px] uppercase tracking-[0.15em] font-mono self-baseline text-charcoal" data-testid={`spec-key-${idx}`}>
                      {row?.key}
                    </dt>
                    <dd className="text-[15px] font-mono self-baseline text-charcoal" data-testid={`spec-value-${idx}`}>
                      {row?.value}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            </>
          )}

          <div className={`hairline ${select ? "border-[#D6C8A3] my-5" : "my-8"}`} />
          {/* iter205 · SELECT PDP OOS notify-me. When a SELECT variant is
              out of stock we swap the entire countdown/vials block for a
              single NOTIFY ME action wired to the existing waitlist. */}
          {select && outOfStock && (
            <div className="mb-6">
              <button
                type="button"
                onClick={toggleWaitlist}
                disabled={waitlistBusy}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] font-mono font-semibold uppercase tracking-[0.22em] border-2 transition-colors shadow-[0_1px_0_rgba(221,169,78,0.35)] disabled:opacity-50 ${
                  onWaitlist
                    ? "border-[#DDA94E] bg-[#EBE0BE] text-[#6E521A] hover:bg-[#DDA94E]/25"
                    : "border-[#DDA94E] bg-[#DDA94E] text-black hover:bg-[#c99333] hover:border-[#c99333]"
                }`}
                data-testid={onWaitlist ? "pdp-oos-notify-on" : "pdp-oos-notify-off"}
                title={onWaitlist ? "You'll get an email when it's back" : "Get notified on next batch"}
              >
                {onWaitlist ? <BellOff size={14} strokeWidth={2.25}/> : <Bell size={14} strokeWidth={2.25}/>}
                {waitlistBusy ? "…" : onWaitlist ? "On list — we'll email you" : "Notify me when it's back"}
              </button>
            </div>
          )}
          {/* iter205 · PDP inventory rationalization — the "N vials in stock"
              tile shows on BOTH SUPPLY and SELECT. On SELECT the countdown
              badge (pdp-stock-badge) still appears alongside it when stock
              is at/below the low-stock threshold — the tile carries the
              authoritative IMS quantity, the badge carries urgency.
              Inventory source (IMS Actual Stock) is unchanged. */}
          {!outOfStock && (() => {
            let vials = null;
            if (hasMgVariants) {
              const selVariant = (product.variants || []).find((v) => String(v.mg) === String(variantMg));
              vials = selVariant?.vials_in_stock;
            } else {
              vials = product.vials_in_stock;
            }
            if (vials === null || vials === undefined) return null;
            const n = Number(vials);
            if (!Number.isFinite(n) || n <= 0) return null;
            return (
              <div className="mb-4">
                <span
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-mono font-semibold uppercase tracking-[0.22em] border-2 ${
                    select
                      ? "bg-[#EBE0BE] text-[#6E521A] border-[#DDA94E] shadow-[0_1px_0_rgba(221,169,78,0.35)]"
                      : "bg-bone text-oxblood border-oxblood/70"
                  }`}
                  data-testid="pdp-vials-in-stock"
                  title="Actual stock (IMS)"
                >
                  <span className={`inline-block h-2 w-2 rounded-full ${select ? "bg-[#DDA94E]" : "bg-oxblood"}`} aria-hidden="true" />
                  {n} vials in stock
                </span>
              </div>
            );
          })()}
          {/* Iter155 — Supply/flat-product PDP countdown badge. Renders for
              any product WITHOUT mg-variants (Supply or Select flat) whose
              product-level vials_in_stock crosses the low/limited thresholds.
              Multi-variant products fall through to the badge inside the
              mg-variant selector below (which is bound to the selected mg). */}
          {!hasMgVariants && product.vials_in_stock !== null && product.vials_in_stock !== undefined && !(select && outOfStock) && (() => {
            const perLow = product.low_stock_threshold;
            const thresh = perLow != null
              ? { lowStockThreshold: Number(perLow), limitedStockThreshold: Math.max(Number(perLow) + 10, stockThresholds.limitedStockThreshold) }
              : stockThresholds;
            const badge = selectStockBadge(Number(product.vials_in_stock), { ...thresh, mgLabel: null });
            if (!badge) return null;
            const isLow = badge.level === "low";
            const cls = select
              ? (isLow
                ? "bg-[#DDA94E] text-black border-[#DDA94E] shadow-[0_0_20px_-4px_rgba(221,169,78,0.55)]"
                : "bg-[#EBE0BE] text-[#6E521A] border-[#DDA94E]/60")
              : (isLow
                ? "bg-oxblood text-bone border-oxblood"
                : "bg-bone text-oxblood border-oxblood/50");
            const dotCls = select
              ? (isLow ? "bg-black animate-pulse" : "bg-[#DDA94E]")
              : (isLow ? "bg-bone animate-pulse" : "bg-oxblood");
            return (
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-mono uppercase tracking-[0.22em] border transition-colors ${cls}`}
                    data-testid="pdp-stock-badge"
                    data-level={badge.level}
                    title="Live inventory"
                  >
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotCls}`} aria-hidden="true" />
                    {badge.label}
                  </span>
                  {isLow && !outOfStock && !select && (
                    <button
                      type="button"
                      onClick={toggleWaitlist}
                      disabled={waitlistBusy}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-[0.2em] transition-colors disabled:opacity-50 ${
                        select
                          ? (onWaitlist
                            ? "border border-[#DDA94E]/60 text-[#6E521A] hover:bg-[#DDA94E]/10"
                            : "border border-[#DDA94E] text-[#6E521A] hover:bg-[#DDA94E] hover:text-black")
                          : (onWaitlist
                            ? "border border-oxblood/40 text-oxblood hover:bg-oxblood hover:text-bone"
                            : "border border-oxblood text-oxblood hover:bg-oxblood hover:text-bone")
                      }`}
                      data-testid={onWaitlist ? "low-stock-notify-on" : "low-stock-notify-off"}
                      title={onWaitlist ? "You'll get an email when it's back" : "Get notified on next batch"}
                    >
                      {onWaitlist ? <BellOff size={11} strokeWidth={2}/> : <Bell size={11} strokeWidth={2}/>}
                      {waitlistBusy ? "…" : onWaitlist ? "On list" : "Notify me"}
                    </button>
                  )}
                </span>
              </div>
            );
          })()}
          {/* Select tier: always surface the mg strength. If the product has
              real mg-variants, render the interactive pill row (buyer picks
              one). If not, but the product carries a dose_mg, render a single
              decorative pill so the buyer never leaves without seeing the mg. */}
          {select && !hasMgVariants && product.dose_mg && (
            <div className="mb-6" data-testid="mg-variant-selector">
              <label className="block text-[13px] uppercase tracking-[0.22em] mb-3 text-[#6E521A]">Variant</label>
              <div className="flex flex-wrap gap-3" data-testid="mg-variant-pills">
                <span
                  className="px-6 py-2.5 rounded-full text-sm font-mono uppercase tracking-widest border border-[#DDA94E] bg-[#DDA94E]/10 text-[#1B1614]"
                  data-testid="mg-variant-pill-static"
                >
                  {product.dose_mg}
                </span>
              </div>
            </div>
          )}
          {hasMgVariants && (
            <div className="mb-6" data-testid="mg-variant-selector">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <label className={`text-[13px] uppercase tracking-[0.22em] font-medium ${select ? "text-[#6E521A]" : "text-charcoal"}`}>
                  {select ? "Variant" : "Vial strength (mg)"}
                </label>
                {/* Iter118 → iter155 — PDP countdown badge. SUPPLY only.
                    On SELECT the authoritative "N vials in stock" tile
                    (pdp-vials-in-stock, rendered above) is sufficient; the
                    redundant "Only N left · [MG]" low-stock badge is
                    suppressed here so 20mg with 11 vials does not show
                    both "11 VIALS IN STOCK" and "ONLY 11 LEFT · 20MG".
                    OOS on SELECT still routes to the dedicated NOTIFY ME
                    block above via product-level outOfStock. */}
                {(() => {
                  if (select) return null;
                  const perLow = product.low_stock_threshold;
                  const thresh = perLow != null
                    ? { lowStockThreshold: Number(perLow), limitedStockThreshold: Math.max(Number(perLow) + 10, stockThresholds.limitedStockThreshold) }
                    : stockThresholds;
                  let vials, mgLbl;
                  if (select) {
                    // iter205 · SELECT out-of-stock swaps to the dedicated
                    // OOS notify-me block above; never render the countdown
                    // as "Only 0 left" for zero stock.
                    if (outOfStock) return null;
                    const selVariant = (product.variants || []).find((v) => String(v.mg) === String(variantMg));
                    if (!selVariant) return null;
                    vials = selVariant.vials_in_stock;
                    mgLbl = null;
                  } else {
                    vials = product.vials_in_stock;
                    mgLbl = null;
                  }
                  if (vials === null || vials === undefined) return null;
                  const badge = selectStockBadge(Number(vials), { ...thresh, mgLabel: mgLbl });
                  if (!badge) return null;
                  const isLow = badge.level === "low";
                  const cls = select
                    ? (isLow
                      ? "bg-[#DDA94E] text-black border-[#DDA94E] shadow-[0_0_20px_-4px_rgba(221,169,78,0.55)]"
                      : "bg-[#EBE0BE] text-[#6E521A] border-[#DDA94E]/60")
                    : (isLow
                      ? "bg-oxblood text-bone border-oxblood"
                      : "bg-bone text-oxblood border-oxblood/50");
                  const dotCls = select
                    ? (isLow ? "bg-black animate-pulse" : "bg-[#DDA94E]")
                    : (isLow ? "bg-bone animate-pulse" : "bg-oxblood");
                  return (
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-mono uppercase tracking-[0.22em] border transition-colors ${cls}`}
                        data-testid="pdp-stock-badge"
                        data-level={badge.level}
                        title="Live inventory"
                      >
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotCls}`} aria-hidden="true" />
                        {isLow && select ? `Only ${badge.count} left · ${formatDose(variantMg, product?.variant_unit)}` : badge.label}
                      </span>
                      {isLow && !outOfStock && !select && (
                        <button
                          type="button"
                          onClick={toggleWaitlist}
                          disabled={waitlistBusy}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-[0.2em] transition-colors disabled:opacity-50 ${
                            select
                              ? (onWaitlist
                                ? "border border-[#DDA94E]/60 text-[#6E521A] hover:bg-[#DDA94E]/10"
                                : "border border-[#DDA94E] text-[#6E521A] hover:bg-[#DDA94E] hover:text-black")
                              : (onWaitlist
                                ? "border border-oxblood/40 text-oxblood hover:bg-oxblood hover:text-bone"
                                : "border border-oxblood text-oxblood hover:bg-oxblood hover:text-bone")
                          }`}
                          data-testid={onWaitlist ? "low-stock-notify-on" : "low-stock-notify-off"}
                          title={onWaitlist ? "You'll get an email when it's back" : "Get notified on next batch"}
                        >
                          {onWaitlist ? <BellOff size={11} strokeWidth={2}/> : <Bell size={11} strokeWidth={2}/>}
                          {waitlistBusy ? "…" : onWaitlist ? "On list" : "Notify me"}
                        </button>
                      )}
                    </span>
                  );
                })()}
              </div>
              {select ? (
                <div className="flex flex-wrap gap-3" data-testid="mg-variant-pills">
                  {(product.variants || []).filter((v) => v && v.price != null).map((v) => {
                    const oos = v.in_stock === false;
                    const mgStr = String(v.mg);
                    const isSelected = variantMg === mgStr;
                    return (
                      <button
                        key={mgStr}
                        type="button"
                        onClick={() => !oos && setVariantMg(mgStr)}
                        disabled={oos}
                        className={`px-6 py-2.5 rounded-full text-sm font-mono uppercase tracking-widest border transition-colors ${
                          isSelected
                            ? "border-[#DDA94E] bg-[#DDA94E]/10 text-[#1B1614]"
                            : oos
                              ? "border-[#D6C8A3] text-[#6E521A]/40 line-through cursor-not-allowed"
                              : "border-[#D6C8A3] text-[#1B1614] hover:border-[#DDA94E]/60"
                        }`}
                        data-testid={`mg-variant-pill-${mgStr}${isSelected ? "-selected" : ""}${oos ? "-oos" : ""}`}
                      >
                        {formatDose(mgStr, product?.variant_unit)}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <Select value={variantMg || ""} onValueChange={(v) => setVariantMg(v)}>
                  <SelectTrigger
                    id="mg-variant-select"
                    className="w-full rounded-2xl border-borderSubtle bg-white text-charcoal font-mono text-[15px] px-4 py-6 focus:border-charcoal focus:ring-0"
                    data-testid="mg-variant-select"
                  >
                    <SelectValue placeholder="Choose vial strength" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-borderSubtle rounded-xl">
                    {(product.variants || []).filter((v) => v && v.price != null).map((v) => {
                      const oos = v.in_stock === false;
                      return (
                        <SelectItem
                          key={String(v.mg)}
                          value={String(v.mg)}
                          disabled={oos}
                          className={`font-mono text-[15px] py-3 px-3 focus:bg-bone/60 data-[state=checked]:bg-charcoal data-[state=checked]:text-bone ${oos ? "text-charcoal/50 line-through" : "text-charcoal"}`}
                          data-testid={`mg-option-${v.mg}${oos ? "-oos" : ""}`}
                        >
                          {oos ? `Out of stock — ${formatDose(v.mg, product?.variant_unit)}` : product?.tier === "supply" ? formatDose(v.mg, product?.variant_unit) : `${formatDose(v.mg, product?.variant_unit)} · $${Number(v.price).toFixed(2)}`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
              {mgOutOfStock && (
                <p className="mt-3 text-[12px] uppercase tracking-[0.22em] text-oxblood font-mono font-medium" data-testid="mg-variant-oos">
                  This strength is currently out of stock.
                </p>
              )}
            </div>
          )}

          {/* Select-tier vial-quantity picker — 3 large pills (1 / 3 / 5 vials).
              Prices auto-compute from the ladder in admin_settings; 3v/5v
              tiles show a gold "Save X%" badge floating above the pill. */}
          {select && (
            <div className="mb-4" data-testid="vial-quantity-selector">
              <p className={`text-[13px] uppercase tracking-[0.22em] mb-3 text-[#6E521A]`}>Vial quantity</p>
              <div className={product.single_buy ? "grid grid-cols-1 gap-x-4 gap-y-8" : "grid grid-cols-3 gap-x-4 gap-y-8"}>
                {[
                  { key: "1vial", n: 1, label: "1 Vial" },
                  { key: "3vial", n: 3, label: "3 Vials" },
                  { key: "5vial", n: 5, label: "5 Vials" },
                  { key: "10vial", n: 10, label: "10 Vials" },
                ]
                  // Single-buy products (e.g. Bacteriostatic Water) are sold
                  // ONE vial at a time — hide the 3-vial / 5-vial kit tiles so
                  // the buyer can't select them. Backend also rejects, but
                  // this is the buyer-facing guard.
                  .filter((opt) => !product.single_buy || opt.key === "1vial" || opt.key === "10vial")
                  .map((opt) => {
                  const isSelected = variant === opt.key;
                  const tilePrice = selectTilePrice(opt.n);
                  const pct = Math.round((Number(selectLadder[opt.n] || 0)) * 100);
                  return (
                    <div key={opt.key} className="relative pt-3">
                      {opt.n !== 1 && pct > 0 && (
                        <span
                          className={`absolute z-10 top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex items-center whitespace-nowrap px-3 py-1 rounded-full text-[12px] font-sans font-semibold uppercase tracking-widest bg-[#DDA94E] text-black`}
                          data-testid={`vial-qty-save-badge-${opt.key}`}
                        >
                          Save {pct}%
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => { setVariant(opt.key); setQty(1); }}
                        className={`w-full text-center rounded-full px-4 py-5 transition-[background-color,border-color,box-shadow] duration-150 border-2 ${
                          isSelected
                            ? "border-[#B8862E] bg-[#DDA94E]/30 text-[#1B1614] shadow-[0_0_0_3px_rgba(221,169,78,0.35)]"
                            : "border-[#D6C8A3] bg-transparent hover:bg-[#F8EFD9] hover:border-[#8A6A2C] hover:shadow-[0_2px_8px_rgba(27,22,20,0.08)] text-[#1B1614]"
                        }`}
                        data-testid={`vial-qty-btn-${opt.key}${isSelected ? "-selected" : ""}`}
                      >
                        <p className="text-base font-serif mb-1">{opt.label}</p>
                        {tilePrice != null ? (
                          <p className="font-mono text-sm text-[#6E521A]">${tilePrice.toFixed(2)}</p>
                        ) : (
                          <p className="font-mono text-xs opacity-70">—</p>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
              {/* iter207.1 · Granny-proof pricing auto-preview — RHM
                  visual polish pass. Stronger gold treatment, larger
                  padding, subtle icon, savings dollar amount rendered in
                  a font-mono chip so it reads immediately. Copy still
                  pulls savings dollars + percentages from the
                  authoritative ladder — no hard-coded discounts. */}
              {(() => {
                if (!canDecompose) return null;
                if (variant !== "1vial") return null;
                if (!preview) return null;
                const pct5 = Math.round((Number(selectLadder[5] || 0)) * 100);
                const pct3 = Math.round((Number(selectLadder[3] || 0)) * 100);
                const nextFive = totalVials < 5
                  ? ladderPreview(5, selectUnitPrice, selectLadder)
                  : null;
                const nextThree = totalVials < 3
                  ? ladderPreview(3, selectUnitPrice, selectLadder)
                  : null;
                if (preview.savings > 0.005) {
                  const unlockedFive = totalVials >= 5 && pct5 > 0;
                  const unlockedThree = !unlockedFive && totalVials >= 3 && pct3 > 0;
                  const headline = unlockedFive
                    ? `5-vial pricing unlocked`
                    : unlockedThree
                      ? `3-vial pricing applied`
                      : `Kit savings applied`;
                  const hint = totalVials === 3 && pct5 > pct3 && nextFive
                    ? `Add 2 more vials to unlock 5-vial pricing (${pct5}% off).`
                    : totalVials === 4 && pct5 > 0
                      ? `1 more vial unlocks 5-vial pricing (${pct5}% off).`
                      : null;
                  return (
                    <div
                      className="mt-4 rounded-2xl border-2 border-[#DDA94E] bg-[#DDA94E]/15 px-5 py-4 flex items-start gap-4 shadow-[0_2px_16px_-6px_rgba(221,169,78,0.55)]"
                      data-testid="ladder-auto-preview"
                    >
                      <span
                        aria-hidden="true"
                        className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#DDA94E] text-black shrink-0"
                      >
                        <Check size={18} strokeWidth={2.75} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <p
                            className="font-serif text-[19px] leading-tight text-[#1B1614]"
                            data-testid="ladder-auto-preview-headline"
                          >
                            {headline}
                          </p>
                          <p
                            className="font-mono text-[15px] font-semibold text-[#6E521A]"
                            data-testid="ladder-auto-preview-savings"
                          >
                            you save ${preview.savings.toFixed(2)}
                          </p>
                        </div>
                        {hint && (
                          <p
                            className="text-[13px] text-[#6E521A] mt-1.5 font-medium"
                            data-testid="ladder-auto-preview-hint"
                          >
                            {hint}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                }
                if (totalVials === 2 && nextThree && nextThree.savings > 0.005) {
                  return (
                    <div
                      className="mt-4 rounded-2xl border-2 border-[#DDA94E]/70 bg-[#EBE0BE] px-5 py-4 flex items-start gap-4 shadow-[0_2px_12px_-6px_rgba(221,169,78,0.35)]"
                      data-testid="ladder-auto-preview-hint-only"
                    >
                      <span
                        aria-hidden="true"
                        className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#EBE0BE] text-[#DDA94E] border-2 border-[#DDA94E] shrink-0"
                      >
                        <Sparkles size={16} strokeWidth={2.5} />
                      </span>
                      <div className="flex-1">
                        <p className="font-serif text-[17px] leading-tight text-[#1B1614]">
                          1 more vial unlocks 3-vial pricing
                        </p>
                        <p className="font-mono text-[13px] text-[#6E521A] mt-1 font-medium">
                          Save {pct3}% at 3 vials
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}

          {isSupplyKit && (
            <div className="mb-6" data-testid="supply-kit-selector">
              <label className="block text-[13px] uppercase tracking-[0.22em] font-medium text-charcoal mb-3">Kit size</label>
              <div className="flex flex-wrap gap-3">
                {[3, 10].map((n) => {
                  const p = supplyKitPrice(n);
                  const unavailable = !(p > 0);
                  const active = supplyKit === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => !unavailable && setSupplyKit(n)}
                      disabled={unavailable}
                      className={`px-6 py-2.5 rounded-full text-sm font-mono uppercase tracking-widest border transition-colors ${
                        active ? "border-oxblood bg-[#F3E4D0] text-charcoal" : unavailable ? "border-borderSubtle text-charcoal/40 cursor-not-allowed" : "border-borderSubtle text-charcoal hover:border-oxblood/60"
                      }`}
                      aria-pressed={active}
                      data-testid={`supply-kit-${n}`}
                    >
                      {n} Vials{p > 0 ? ` · $${p.toFixed(2)}` : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-stretch gap-4 mb-4">
            <div className={`flex items-center rounded-full overflow-hidden ${select ? "border-2 border-[#8A6A2C]/60 bg-[#F8EFD9]" : "border border-charcoal/30"}`}>
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className={`w-11 h-11 text-lg transition-colors ${select ? "text-[#1B1614] hover:bg-[#DDA94E] hover:text-black" : "hover:bg-charcoal hover:text-bone"}`}
                data-testid="qty-decrement"
                aria-label="Decrease quantity"
              >−</button>
              <span className={`w-10 text-center font-mono text-sm ${select ? "text-[#1B1614] font-semibold text-base" : ""}`} data-testid="qty-value">{qty}</span>
              <button
                onClick={() => {
                  const cap = product.max_qty_per_order;
                  if (cap && qty >= cap) {
                    // Instead of silently swallowing the click on a disabled
                    // button, tell the buyer exactly why the stepper won't go.
                    const msg = cap === 1
                      ? `One vial maximum — ${product.name} is sold one vial at a time.`
                      : `${product.name} is limited to ${cap} per order.`;
                    toast.info(msg);
                    return;
                  }
                  setQty((q) => (cap && q + 1 > cap ? cap : q + 1));
                }}
                className={`w-11 h-11 text-lg transition-colors ${select ? "text-[#1B1614] hover:bg-[#DDA94E] hover:text-black" : "hover:bg-charcoal hover:text-bone"} ${!!(product.max_qty_per_order && qty >= product.max_qty_per_order) ? "opacity-40" : ""}`}
                data-testid="qty-increment"
                aria-label="Increase quantity"
              >+</button>
            </div>
            <HoverCard openDelay={100} closeDelay={80}>
              <HoverCardTrigger asChild>
                {isTempViewer ? (
                  <span
                    className="flex-1 inline-flex items-center justify-center gap-2 tracking-[0.22em] text-xs uppercase py-3.5 rounded-full bg-stone/40 text-bone cursor-not-allowed"
                    data-testid="add-to-cart-btn-preview"
                  >
                    Preview only · contact us
                  </span>
                ) : (
                  <button
                    onClick={() => {
                      if (outOfStock) { toast.error("Out of stock"); return; }
                      if (hasMgVariants && mgOutOfStock) { toast.error("This mg strength is out of stock"); return; }
                      if (select && selectUnitPrice == null) { toast.error("Unit price unavailable"); return; }
                      const cap = product.max_qty_per_order;
                      const finalQty = cap ? Math.min(qty, cap) : qty;
                      if (isSupplyKit) {
                        const ok = add(product, finalQty, {
                          variant: supplyKit === 10 ? "10vial" : "3vial",
                          variant_mg: hasMgVariants ? variantMg : undefined,
                          price: displayPrice,
                          unit_label: (product.variants || []).length >= 2 && variantMg ? `${variantMg}${product.variant_unit || "mg"} · ${supplyKit} Vial Kit` : `${supplyKit} Vial Kit`,
                        });
                        if (ok !== false) toast.success(`${product.name}${(product.variants || []).length >= 2 && variantMg ? ` ${formatDose(variantMg, product.variant_unit)}` : ""} · ${supplyKit}-vial kit × ${finalQty} added`);
                      } else if (select) {
                        // iter207 · Granny-proof pricing. Emit the tile as-is
                        // + unit price + effective ladder; CartContext
                        // aggregates into the (product, variant_mg) bucket and
                        // rebalances into the best-eligible authoritative
                        // decomposition. Server re-prices on submit via the
                        // same _resolve_select_line_price path.
                        const ok = add(product, finalQty, {
                          variant,
                          variant_mg: hasMgVariants ? variantMg : undefined,
                          price: selectTilePrice(selectVialCount),  // display-only hint
                          unit_price: selectUnitPrice,
                          effective_ladder: { 3: Number(selectLadder[3] || 0), 5: Number(selectLadder[5] || 0), 10: Number(selectLadder[10] || 0) },
                        });
                        const totalV = selectVialCount * finalQty;
                        if (ok !== false) toast.success(`${totalV} vial${totalV === 1 ? "" : "s"} of ${product.name} added`);
                      } else if (hasMgVariants) {
                        const ok = add(product, finalQty, {
                          variant_mg: variantMg,
                          price: Number(selectedMgVariant.price),
                          unit_label: `${formatDose(variantMg, product?.variant_unit)}`,
                        });
                        if (ok !== false) toast.success(`${product.name} · ${formatDose(variantMg, product?.variant_unit)} × ${finalQty} added`);
                      } else {
                        // iter185.2 — Supply fixed-tier: server re-resolves the
                        // price from supply_tier_id, but the local cart needs a
                        // truthful `price` so the running subtotal on /cart
                        // matches the shelf price.
                        const ok = add({ ...product, price: displayPrice }, finalQty);
                        if (ok !== false) toast.success(`${product.name} × ${finalQty} added`);
                      }
                    }}
                    disabled={cannotAdd}
                    className={`flex-1 inline-flex items-center justify-center gap-2 tracking-[0.22em] text-xs uppercase py-3.5 rounded-full transition-colors ${
                      cannotAdd
                        ? "bg-stone/40 text-bone cursor-not-allowed"
                        : select
                          ? "bg-[#DDA94E] text-black hover:bg-[#E8B857]"
                          : "bg-charcoal text-bone hover:bg-oxblood"
                    }`}
                    data-testid="add-to-cart-btn"
                  >
                    <Plus size={14} strokeWidth={2.5}/>
                    {outOfStock ? "Out of stock" : <>Add · ${Number(decomposedTotal || 0).toFixed(2)}</>}
                  </button>
                )}
              </HoverCardTrigger>
              {/* iter156.9 — the "Research Use Only · Untested" hover warning
                  was removed. All BBK products are tested; the warning was
                  factually incorrect and eroding buyer confidence. */}
            </HoverCard>
          </div>
          {product.max_qty_per_order && (
            <p className={`text-[13px] font-mono uppercase tracking-[0.22em] mb-2 ${select ? "text-[#6E521A]" : "text-stone"}`} data-testid="pd-max-qty-hint">
              Limit {product.max_qty_per_order} per order
            </p>
          )}

          {/* COA list — buyer sees a flat list of every COA on file. Single-COA
              products render as a single link; multi-COA products render each
              certificate as its own row so buyers can pick the lot they need.
              Labels are human-readable ({name} · {mg}) rather than raw
              filenames per 2026-02-16 site-optimization pass. The COA that
              matches the currently-selected variant is visually prioritized. */}
          {!featured && (product.coas || []).length > 0 && (
            <div
              className={`mb-6 rounded-lg overflow-hidden ${
                select ? "border border-[#DDA94E]/50 bg-[#F5EDD5]" : "border border-amber-300 bg-amber-50"
              }`}
              data-testid="product-detail-coa-block"
            >
              <div className={`flex items-center gap-3 px-4 py-3 ${(product.coas || []).length > 1 ? "border-b " + (select ? "border-[#DDA94E]/30" : "border-amber-200") : ""}`}>
                <FileCheck2 size={18} strokeWidth={2} className={`shrink-0 ${select ? "text-[#6E521A]" : "text-amber-800"}`} />
                <div className="flex-1">
                  <p className={`text-sm font-semibold leading-tight ${select ? "text-[#1B1614]" : "text-amber-950"}`}>Latest Test Results</p>
                  <p className={`text-[13px] mt-0.5 ${select ? "text-[#6E521A]" : "text-amber-800/80"}`}>
                    {(product.coas || []).length === 1
                      ? "Verified Certificate of Analysis on file"
                      : `${(product.coas || []).length} verified Certificates of Analysis on file`}
                  </p>
                </div>
              </div>
              <ul className={`divide-y ${select ? "divide-[#DDA94E]/25" : "divide-amber-200/60"}`} data-testid="product-detail-coa-list">
                {(() => {
                  // Humanize: prefer a "{ProductName} · {mg}mg" label. Try to
                  // extract mg from the filename (e.g. `GHK50-…` → 50mg).
                  const baseName = String(product.name || "").replace(/\s*\d+\s*mg\s*$/i, "").trim() || "Certificate";
                  const activeMg = selectedMgVariant ? String(selectedMgVariant.mg) : null;
                  const withLabels = (product.coas || []).map((c) => {
                    let mg = null;
                    const fn = String(c.filename || "");
                    const m = fn.match(/(\d+)\s*mg/i) || fn.match(/([A-Z]{2,}?)(\d+)/);
                    if (m) mg = m[2] || m[1];
                    return {
                      ...c,
                      _mg: mg,
                      _label: mg ? `${baseName} · ${mg}mg` : baseName,
                    };
                  });
                  // Move the matching-variant COA to the top when detectable
                  // so the buyer sees "their" lot first — never hide the rest.
                  const ordered = activeMg
                    ? [...withLabels].sort((a, b) => {
                        const am = String(a._mg || "");
                        const bm = String(b._mg || "");
                        if (am === activeMg && bm !== activeMg) return -1;
                        if (bm === activeMg && am !== activeMg) return 1;
                        return 0;
                      })
                    : withLabels;
                  return ordered.map((c) => {
                    const API = process.env.REACT_APP_BACKEND_URL || "";
                    const href = `${API}/api/products/${product.id}/coa/${c.id}`;
                    const isMatch = activeMg && String(c._mg || "") === activeMg;
                    return (
                      <li key={c.id}>
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${select ? "hover:bg-[#EBE0BE]" : "hover:bg-amber-100"} ${
                            isMatch ? (select ? "bg-[#EBE0BE]/70" : "bg-amber-100/70") : ""
                          }`}
                          title={c.filename || c._label}
                          data-testid={`product-detail-coa-item-${c.id}`}
                        >
                          <span className={`flex-1 truncate text-[13px] font-mono ${select ? "text-[#1B1614]" : "text-amber-950"}`}>
                            {c._label}
                            {isMatch && (
                              <span className={`ml-2 text-[10px] font-mono uppercase tracking-[0.22em] ${select ? "text-[#6E521A]" : "text-amber-900"}`}>
                                · matches selection
                              </span>
                            )}
                          </span>
                          <span className={`text-[13px] uppercase tracking-[0.22em] underline underline-offset-4 shrink-0 ${
                            select ? "text-[#6E521A] decoration-[#DDA94E]" : "text-amber-900 decoration-amber-500/50"
                          }`}>
                            View
                          </span>
                        </a>
                      </li>
                    );
                  });
                })()}
              </ul>
            </div>
          )}

          {/* Spec sheet — Select tier only, placed below Latest Test Results, no heading */}
          {!featured && select && Array.isArray(product.spec) && product.spec.length > 0 && (
            <div className="mt-6" data-testid="select-spec-sheet">
              <dl className="grid grid-cols-[max-content,1fr] gap-x-6 gap-y-2" data-testid="spec-sheet-table">
                {product.spec.map((row, idx) => (
                  <React.Fragment key={idx}>
                    <dt className="text-[13px] uppercase tracking-[0.15em] font-mono self-baseline text-[#6E521A]" data-testid={`spec-key-${idx}`}>
                      {row?.key}
                    </dt>
                    <dd className="text-[15px] font-mono self-baseline text-[#1B1614]" data-testid={`spec-value-${idx}`}>
                      {row?.value}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}

          {outOfStock && (
            <div className="mb-4 bg-oxblood/5 border border-oxblood/40 border-l-[4px] border-l-oxblood rounded-r-2xl rounded-l-sm p-5" data-testid="oos-waitlist-block">
              <div className="flex items-start gap-3">
                <Bell size={16} strokeWidth={1.75} className="text-oxblood shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg leading-tight mb-1">
                    {onWaitlist ? "You're on the waitlist." : "Get notified when it's back."}
                  </p>
                  <p className="text-xs text-stone leading-relaxed">
                    {onWaitlist
                      ? "We'll email + notify you the moment we restock this kit."
                      : "One tap adds you to the restock list. No spam — we email once, when it's back."}
                  </p>
                </div>
                <button
                  onClick={toggleWaitlist}
                  disabled={waitlistBusy}
                  className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 tracking-[0.22em] text-[12px] uppercase transition-colors rounded-full disabled:opacity-50 ${
                    onWaitlist
                      ? "border border-oxblood/40 text-oxblood hover:bg-oxblood hover:text-bone"
                      : "bg-oxblood text-bone hover:bg-charcoal"
                  }`}
                  data-testid={onWaitlist ? "leave-waitlist-btn" : "join-waitlist-btn"}
                >
                  {onWaitlist ? <BellOff size={12} strokeWidth={2}/> : <Bell size={12} strokeWidth={2}/>}
                  {waitlistBusy ? "…" : onWaitlist ? "Leave" : "Notify me"}
                </button>
              </div>
            </div>
          )}

          {!select && (
          <p className={`text-[12px] mt-6 font-mono uppercase tracking-[0.2em] text-charcoal/70`} data-testid="min-order-notice-removed-spacer">
            {/* iter184 — 5-kit minimum callout removed. New Supply model is fixed-tier
                pricing with no order minimum. Placeholder kept so surrounding margin math
                stays consistent; will be re-purposed if a per-tier rule ever returns. */}
          </p>
          )}

          {!featured && (
          <p className={`text-[12px] mt-6 font-mono uppercase tracking-[0.2em] ${select ? "text-[#6E521A]" : "text-charcoal"}`}>Ships from a temperature-controlled facility · Research use only</p>
          )}
        </div>
      </div>

      {/* Featured-mode lower section — mirrors the mock: Latest Test Results
          on the left, molecule/spec details on the right, ships-line spans
          full width beneath. Only rendered for featured=true so standalone
          PDPs remain unchanged (the same content still renders inside the
          right column via the gates above). */}
      {featured && (
        <div className="mt-8" data-testid="featured-below-fold">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
            {(product.coas || []).length > 0 ? (
              <div
                className={`rounded-lg overflow-hidden ${
                  select ? "border border-[#DDA94E]/50 bg-[#F5EDD5]" : "border border-amber-300 bg-amber-50"
                }`}
                data-testid="featured-coa-block"
              >
                <div className={`flex items-center gap-3 px-4 py-3 ${(product.coas || []).length > 1 ? "border-b " + (select ? "border-[#DDA94E]/30" : "border-amber-200") : ""}`}>
                  <FileCheck2 size={18} strokeWidth={2} className={`shrink-0 ${select ? "text-[#6E521A]" : "text-amber-800"}`} />
                  <div className="flex-1">
                    <p className={`text-sm font-semibold leading-tight ${select ? "text-[#1B1614]" : "text-amber-950"}`}>Latest Test Results</p>
                    <p className={`text-[13px] mt-0.5 ${select ? "text-[#6E521A]" : "text-amber-800/80"}`}>
                      {(product.coas || []).length === 1
                        ? "Verified Certificate of Analysis on file"
                        : `${(product.coas || []).length} verified Certificates of Analysis on file`}
                    </p>
                  </div>
                </div>
                <ul className={`divide-y ${select ? "divide-[#DDA94E]/25" : "divide-amber-200/60"}`} data-testid="featured-coa-list">
                  {(() => {
                    const baseName = String(product.name || "").replace(/\s*\d+\s*mg\s*$/i, "").trim() || "Certificate";
                    const activeMg = selectedMgVariant ? String(selectedMgVariant.mg) : null;
                    const withLabels = (product.coas || []).map((c) => {
                      let mg = null;
                      const fn = String(c.filename || "");
                      const m = fn.match(/(\d+)\s*mg/i) || fn.match(/([A-Z]{2,}?)(\d+)/);
                      if (m) mg = m[2] || m[1];
                      return { ...c, _mg: mg, _label: mg ? `${baseName} · ${mg}mg` : baseName };
                    });
                    const ordered = activeMg
                      ? [...withLabels].sort((a, b) => {
                          const am = String(a._mg || "");
                          const bm = String(b._mg || "");
                          if (am === activeMg && bm !== activeMg) return -1;
                          if (bm === activeMg && am !== activeMg) return 1;
                          return 0;
                        })
                      : withLabels;
                    return ordered.map((c) => {
                      const API = process.env.REACT_APP_BACKEND_URL || "";
                      const href = `${API}/api/products/${product.id}/coa/${c.id}`;
                      const isMatch = activeMg && String(c._mg || "") === activeMg;
                      return (
                        <li key={c.id}>
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${select ? "hover:bg-[#EBE0BE]" : "hover:bg-amber-100"} ${
                              isMatch ? (select ? "bg-[#EBE0BE]/70" : "bg-amber-100/70") : ""
                            }`}
                            title={c.filename || c._label}
                            data-testid={`featured-coa-item-${c.id}`}
                          >
                            <span className={`flex-1 truncate text-[13px] font-mono ${select ? "text-[#1B1614]" : "text-amber-950"}`}>
                              {c._label}
                              {isMatch && (
                                <span className={`ml-2 text-[10px] font-mono uppercase tracking-[0.22em] ${select ? "text-[#6E521A]" : "text-amber-900"}`}>
                                  · matches selection
                                </span>
                              )}
                            </span>
                            <span className={`text-[13px] uppercase tracking-[0.22em] underline underline-offset-4 shrink-0 ${
                              select ? "text-[#6E521A] decoration-[#DDA94E]" : "text-amber-900 decoration-amber-500/50"
                            }`}>
                              View
                            </span>
                          </a>
                        </li>
                      );
                    });
                  })()}
                </ul>
              </div>
            ) : (
              <div
                className={`rounded-lg overflow-hidden px-4 py-3 flex items-center gap-3 ${select ? "border border-[#DDA94E]/50 bg-[#F5EDD5]" : "border border-amber-300 bg-amber-50"}`}
                data-testid="featured-coa-block-empty"
              >
                <FileCheck2 size={18} strokeWidth={2} className={`shrink-0 ${select ? "text-[#6E521A]" : "text-amber-800"}`} />
                <div className="flex-1">
                  <p className={`text-sm font-semibold leading-tight ${select ? "text-[#1B1614]" : "text-amber-950"}`}>Latest Test Results</p>
                  <p className={`text-[13px] mt-0.5 ${select ? "text-[#6E521A]" : "text-amber-800/80"}`}>
                    No certificates on file
                  </p>
                </div>
              </div>
            )}
            {Array.isArray(product.spec) && product.spec.length > 0 ? (
              <div data-testid="featured-spec-sheet">
                <dl className="grid grid-cols-[max-content,1fr] gap-x-6 gap-y-2" data-testid="featured-spec-sheet-table">
                  {product.spec.map((row, idx) => (
                    <React.Fragment key={`f-${idx}`}>
                      <dt className={`text-[13px] uppercase tracking-[0.15em] font-mono self-baseline ${select ? "text-[#6E521A]" : "text-charcoal"}`} data-testid={`featured-spec-key-${idx}`}>
                        {row?.key}
                      </dt>
                      <dd className={`text-[15px] font-mono self-baseline ${select ? "text-[#1B1614]" : "text-charcoal"}`} data-testid={`featured-spec-value-${idx}`}>
                        {row?.value}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            ) : (
              <div className={`text-[13px] font-mono italic ${select ? "text-[#6E521A]/70" : "text-charcoal/60"}`} data-testid="featured-spec-empty">
                Molecule details unavailable
              </div>
            )}
          </div>
          {/* Ships line text removed per spec; wrapper retained with a
              non-breaking space so the mt-6 + line-height footprint of the
              Featured lower section is preserved and nothing shifts. */}
          <p
            className={`text-center text-[12px] mt-6 font-mono uppercase tracking-[0.2em] ${select ? "text-[#6E521A]" : "text-charcoal"}`}
            aria-hidden="true"
          >
            {"\u00A0"}
          </p>
        </div>
      )}

      {/* Related Products — admin-curated, up to 4. Rendered only when
          the backend hydrated at least one. Kept intentionally minimal:
          image + name + View Product link. No price, no COA, no LYO,
          no Add to Cart per 2026-02-16 site-optimization spec. */}
      {!embedded && Array.isArray(product.related_products) && product.related_products.length > 0 && (
        <section
          className={`mt-16 pt-10 border-t ${select ? "border-[#D6C8A3]" : "border-borderSubtle"}`}
          data-testid="related-products-section"
        >
          <p className={`text-[12px] font-mono uppercase tracking-[0.32em] mb-2 ${select ? "text-[#6E521A]" : "text-oxblood"}`}>
            You may also like
          </p>
          <h2 className={`font-serif text-3xl md:text-4xl tracking-tight mb-8 ${select ? "text-[#1B1614]" : "text-charcoal"}`}>
            Related products
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {product.related_products.slice(0, 4).map((rp) => {
              const rpTier = rp.tier || tier || "";
              const rpHref = rpTier ? `/shop/${rp.slug}?tier=${rpTier}` : `/shop/${rp.slug}`;
              return (
                <Link
                  key={rp.id}
                  to={rpHref}
                  className={`group flex flex-col rounded-2xl overflow-hidden border transition-all hover:-translate-y-0.5 ${
                    select
                      ? "bg-[#E8DDBB] border-[#DDA94E]/60 hover:border-[#DDA94E] hover:shadow-[0_4px_28px_-6px_rgba(221,169,78,0.35)]"
                      : "bg-white border-borderSubtle hover:shadow-[0_2px_20px_-4px_rgba(0,0,0,0.08)]"
                  }`}
                  data-testid={`related-product-${rp.slug}`}
                >
                  {/* iter206 · Related Products image well — the <img> and
                      placeholder must be absolutely positioned so they paint
                      ABOVE `.select-photo-well::before` (position:absolute;
                      z-index:0). Without `absolute inset-0` the img renders as
                      a static-flow element and is covered by the dark radial
                      gradient in the normal state; hover only "worked" because
                      group-hover:scale creates a new stacking context via
                      transform. This matches the pattern already used by the
                      PDP hero (line 259) and the Catalog featured card
                      (Catalog.jsx:471) — the two other consumers of the well. */}
                  <div className={`relative aspect-square overflow-hidden ${select ? "select-photo-well" : "bg-charcoal"}`}>
                    {rp.image_url ? (
                      <img
                        src={rp.image_url}
                        alt={rp.name}
                        className={`absolute inset-0 w-full h-full object-contain group-hover:scale-[1.04] transition-transform duration-500 ${select ? "p-10" : "p-6"}`}
                        draggable={false}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className={`font-serif text-6xl leading-none ${select ? "text-[#6E521A]/10" : "text-bone/10"}`}>★</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col gap-3 flex-1">
                    <h3 className={`font-serif text-lg leading-tight ${select ? "text-[#1B1614]" : "text-charcoal"}`}>
                      {String(rp.name || "").replace(/\s*\d+\s*mg\s*$/i, "").trim()}
                    </h3>
                    <span className={`mt-auto inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.22em] font-mono ${select ? "text-[#6E521A]" : "text-oxblood"}`}>
                      View Product →
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Lightbox removed — every PDP image is now non-interactive. */}
    </div>
  );
}
